import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { WashingtonSilhouette } from "./Logo";

const copyEmail = () => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard
      .writeText("sales@northwesttitlealliance.com")
      .then(() => toast.success("Email address copied — paste it into your mail app."))
      .catch(() => toast.info("Email us at sales@northwesttitlealliance.com"));
  } else {
    toast.info("Email us at sales@northwesttitlealliance.com");
  }
};

const EASE = [0.16, 1, 0.3, 1];

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const CircuitField = () => (
  <svg
    className="absolute inset-0 w-full h-full opacity-[0.35]"
    viewBox="0 0 1440 900"
    fill="none"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <g stroke="#3E5C76" strokeWidth="1">
      <path className="trace-draw" d="M-20 720 H320 L420 620 H760 L860 520 H1200 L1300 420 H1500" />
      <path className="trace-draw" d="M-20 780 H260 L360 680 H700 L800 580 H1180" style={{ animationDelay: "-8s" }} />
      <path className="trace-draw" d="M900 920 V700 L1000 600 V380 L1100 280 V60" style={{ animationDelay: "-14s" }} />
      <path d="M-20 660 H240 L340 560 H640" opacity="0.5" />
    </g>
    <g className="circuit-pulse">
      <circle cx="420" cy="620" r="5" stroke="#38bdf8" strokeWidth="1.5" />
      <circle cx="860" cy="520" r="5" stroke="#38bdf8" strokeWidth="1.5" />
      <circle cx="1100" cy="280" r="5" stroke="#38bdf8" strokeWidth="1.5" />
      <circle cx="360" cy="680" r="4" stroke="#2176C7" strokeWidth="1.5" />
    </g>
  </svg>
);

const GridLines = () => (
  <div className="absolute inset-0 pointer-events-none max-w-[1400px] mx-auto px-6 md:px-12 hidden md:grid grid-cols-12" aria-hidden="true">
    {Array.from({ length: 12 }).map((_, i) => (
      <div key={`grid-col-${i + 1}`} className="border-l border-steel/10" />
    ))}
  </div>
);

const HeroTagline = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1, delay: 0.15 }}
    className="flex items-center gap-4 mb-10 md:mb-14"
  >
    <span className="h-px w-12 bg-tech" />
    <span className="font-mono-accent text-[10px] md:text-[11px] tracking-[0.35em] text-tech-bright uppercase" data-testid="hero-tagline">
      AI · Software · Innovation
    </span>
  </motion.div>
);

const HeroHeading = () => (
  <h1 className="font-display font-bold tracking-[-0.03em] leading-[0.95] text-[12vw] md:text-[6.4vw] lg:text-[5vw] uppercase">
    <RevealLine delay={0.35}>
      <span className="text-fog">Northwest</span>
    </RevealLine>
    <RevealLine delay={0.5}>
      <svg className="roots-svg" data-testid="hero-roots-outline" role="img" aria-label="Roots.">
        <text x="0" y="84%">ROOTS.</text>
      </svg>
    </RevealLine>
    <RevealLine delay={0.65}>
      <span className="reach-line" data-testid="hero-reach-line">
        <span className="reach-national text-fog">National </span>
        <span className="reach-bright text-tech-bright">Reach.</span>
      </span>
    </RevealLine>
  </h1>
);

const HeroCtas = () => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
    className="md:col-span-6 lg:col-span-4 flex flex-wrap gap-4"
  >
    <a
      href="#products"
      data-testid="hero-cta-products"
      className="group relative px-7 py-4 bg-tech text-fog font-mono-accent text-[11px] tracking-[0.22em] uppercase overflow-hidden"
    >
      <span className="absolute inset-0 bg-tech-bright translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
      <span className="relative flex items-center gap-2">
        Explore AXIOM <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform duration-300" />
      </span>
    </a>
    <a
      href="#about"
      data-testid="hero-cta-alliance"
      className="px-7 py-4 border border-steel text-fog/80 hover:border-tech-bright hover:text-tech-bright font-mono-accent text-[11px] tracking-[0.22em] uppercase transition-colors duration-300"
    >
      The Alliance
    </a>
    <div
      className="w-full pt-4 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono-accent text-[9px] sm:text-[10px] tracking-[0.12em] sm:tracking-[0.18em] uppercase text-fog/40"
      data-testid="hero-contact-strip"
    >
      <a
        href="mailto:sales@northwesttitlealliance.com"
        onClick={copyEmail}
        data-testid="hero-sales-email"
        className="hover:text-tech-bright transition-colors duration-300"
      >
        sales@northwesttitlealliance.com
      </a>
      <span className="h-1 w-1 rotate-45 bg-tech/60 hidden lg:inline-block" aria-hidden="true" />
      <a
        href="tel:+12534531754"
        data-testid="hero-sales-phone"
        className="hover:text-tech-bright transition-colors duration-300"
      >
        (253) 453-1754
      </a>
    </div>
  </motion.div>
);

const ScrollCue = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 1.8, duration: 1 }}
    className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
    aria-hidden="true"
  >
    <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
      <ArrowDown className="h-4 w-4 text-fog/40" />
    </motion.div>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 42, damping: 16, mass: 0.6 });
  const sy = useSpring(my, { stiffness: 42, damping: 16, mass: 0.6 });
  const monoX = useTransform(sx, [-0.5, 0.5], [-22, 22]);
  const monoY = useTransform(sy, [-0.5, 0.5], [-14, 14]);
  const monoRotX = useTransform(sy, [-0.5, 0.5], [7, -7]);
  const monoRotY = useTransform(sx, [-0.5, 0.5], [-9, 9]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} id="top" onMouseMove={onMouseMove} className="relative min-h-screen overflow-hidden bg-navy grain" data-testid="hero-section">
      {/* Parallax forest backdrop */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src="/assets/hero-forest.jpg"
          alt=""
          className="w-full h-[120%] object-cover object-bottom opacity-40"
        />
        <div className="absolute inset-0 bg-navy/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/40 to-navy" />
      </motion.div>

      <CircuitField />

      {/* Original monogram — signature on-load reveal + mouse-drift 3D */}
      <div className="absolute inset-0 hidden lg:flex items-center justify-end pointer-events-none select-none" aria-hidden="true">
        <motion.div
          style={{
            x: monoX,
            y: monoY,
            rotateX: monoRotX,
            rotateY: monoRotY,
            transformPerspective: 1000,
          }}
          className="will-change-transform"
        >
          <motion.img
            src="/assets/nwta-monogram.png"
            alt=""
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 0.16, scale: 1 }}
            transition={{ duration: 1.8, delay: 0.8, ease: EASE }}
            className="w-[42vw] max-w-[680px] mr-[-2%] drop-shadow-[0_0_60px_rgba(33,118,199,0.25)]"
          />
        </motion.div>
      </div>

      {/* Vertical grid lines */}
      <GridLines />

      <motion.div
        style={{ y: fgY, opacity: fade }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-40 md:pt-48 pb-24 flex flex-col min-h-screen"
      >
        <HeroTagline />

        <HeroHeading />

        <div className="mt-auto pt-16 md:pt-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="md:col-span-6 lg:col-span-5 text-base md:text-lg text-fog/70 leading-relaxed max-w-md"
            data-testid="hero-subcopy"
          >
            Underwriting-grade AI for the title industry — built in the Pacific
            Northwest by people who lived the work, deployed for professionals
            in all fifty states.
          </motion.p>

          <HeroCtas />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="md:col-span-12 lg:col-span-3 flex lg:justify-end items-center gap-3"
          >
            <WashingtonSilhouette className="h-6 w-auto opacity-80" />
          </motion.div>
        </div>
      </motion.div>

      <ScrollCue />
    </section>
  );
};
